import { FieldInputProps } from "formik";
import { Schema, reach, ObjectSchema, StringSchema, ArraySchema } from "yup";
import { XmlStringFieldType } from "./validations";

/**
 * Used for any type that is not an array.
 * Name: The full name from the tree to reference the field in a form.
 * Label: The localization key to lookup when describing the field to a user.
 */
export type TypesafeFieldProps = Pick<FieldInputProps<any>, "name"> & {
  "data-testid": string;
  id: string;
  label: string;
};

/**
 * Arrays and Objects recurse down their keys/indexes.
 * Simple types are the end of the chain and become `TypesafeFieldProps`.
 *
 */
// prettier-ignore Prettier will throw them all in a flat line, stop it, its easier to read as typeCondition => type\n
type TypeSafeEntry<T> = T extends Date
  ? TypesafeFieldProps
  : T extends Array<infer P>
  ? TypeSafeArray<P>
  : T extends object
  ? SchemaObject<T>
  : TypesafeFieldProps;

/**
 * Arrays are special compared to every other type.
 * We cannot know at compile time what a valid length to the array is.
 * When using array props, you have to invoke the key method to get the indexed location for Formik,
 * AND also not run out of bounds.
 * Therefore, expose a get(number) method to act effectively like an array.
 *
 * It also exposes Name and Label from TypesafeFieldProps, if you have a list of things, they are probably
 * labeled as a group. in case you need group level labels. eg:
 * User
 * - User[0]
 * - User[1]
 * - User[2]
 */
type TypeSafeArray<T> = {
  get: (i: number) => TypeSafeEntry<T>;
} & TypesafeFieldProps;

/**
 * Iterate every key in a type.
 * https://www.typescriptlang.org/docs/handbook/advanced-types.html#mapped-types
 */
type SchemaObject<T> = {
  [P in keyof T]-?: TypeSafeEntry<NonNullable<T[P]>>;
};
/**
 * A type the exposes the form fields that match a given schema type, for example
 * const simpleSchema = yup.object({
 *   field: yup.string()
 *     .label("My Field Name")
 * });
 * Would map to a type that matches this object
 * {
 *   field: {
 *     name: "field",
 *     label: "My Field Name"
 *   }
 * }
 * To use, simply expose the Infered type from yup and use that as the generic so for the above
 *
 * type SimpleType = InferType<typeof  simpleSchema>
 * const expectedFieldProps: SchemaFields<SimpleType> = {
 *   field: {
 *      id: "field",
 *     name: "field",
 *     label: "My Field Name",
 *     "data-testid": "field"
 *   }
 * }
 * You do not have to build the values by hand, in fact I suggest you don't.
 * Use the `fromSchema` method to produce the types.  You do not even have to do all the generic filling I described
 * above so
 *   `const props = fromSchema(simpleSchema)`
 * TS will automatically infer the type for you.
 *
 * From there, all the props are splattable into Formik's <Fields>
 */
export type SchemaFields<T> = T extends Array<infer P>
  ? TypeSafeArray<P>
  : SchemaObject<T>;

/**
 * Enumerate our supported types, if we add more types to yup's schema, we should add them here.
 * If a type is not listed as supported, its going to be treated as a basic type like number/string
 * and provide the FieldProps at its level.
 */
type SupportedMaps =
  | "object"
  | "date"
  | "string"
  | "array"
  | "boolean"
  | "number"
  | "mixed"
  | XmlStringFieldType;
/**
 * Map from string -> the types we support.
 * This lets us compile check looking up methods in an object that are keyed off `SupportedMaps`
 */
const lookup: Record<string | SupportedMaps, SupportedMaps | null> = {
  object: "object",
  string: "string",
  boolean: "boolean",
  number: "number",
  array: "array",
  date: "date",
  xmlString: "xmlString",
  mixed: "mixed"
};
type PrimitiveHandle = (schema: StringSchema) => TypesafeFieldProps;
type ObjectHandle = <T extends object>(
  schema: ObjectSchema<T>
) => SchemaObject<T>;
type ArrayHandle = <T>(schema: ArraySchema<T>) => TypeSafeArray<T>;

//Now define a type that will map all supported types that we have.  `SupportedMaps` is out key
//This type forces us to have something to lookup for every type.
type MapperRequireKeys = {
  [key in SupportedMaps]: key extends "object"
    ? ObjectHandle
    : key extends "array"
    ? ArrayHandle
    : PrimitiveHandle;
};

interface TreeContext {
  fieldPath: string;
}

const pathToLabel = (fieldPath: string) =>
  fieldPath.split(".").pop() || fieldPath;

const genericHandle = ({ fieldPath }: TreeContext): PrimitiveHandle => (
  s: Schema<any>
): TypesafeFieldProps => {
  const { label } = s.describe();
  return {
    "data-testid": fieldPath,
    name: fieldPath,
    id: fieldPath,
    label: label || pathToLabel(fieldPath)
  };
};

/**
 * A method to return a Mapper for a schema.
 * Allows us to traverse recursively down the schema tree.
 * @param fieldPath
 */
const schemaMappers = (
  { fieldPath }: TreeContext = { fieldPath: "" }
): MapperRequireKeys => {
  function mapInnerSchema<T>(
    nestMapper: MapperRequireKeys,
    nextSchema: Schema<any>
  ): TypeSafeEntry<T> | null {
    const { type } = nextSchema.describe();
    const idx: SupportedMaps | null = lookup[type];
    if (idx) {
      const nest = nestMapper[idx];
      // @ts-ignore the problem is the schema type.
      return nest(nextSchema);
    }
    return null;
  }

  return {
    object: <T extends object>(schema: ObjectSchema<T>): SchemaObject<T> => {
      const { fields } = schema.describe();
      const keys = Object.keys(fields);
      const mapField = (k: string): [string, TypesafeFieldProps] => {
        const contextPath = fieldPath ? `${fieldPath}.${k}` : k;
        const reachInto = () => {
          try {
            return reach(schema, k);
          } catch (e) {
            console.error("Failed at", contextPath);
            throw e;
          }
        };
        const nextSchema: Schema<any> = reachInto();
        const { label } = nextSchema.describe();
        const nestMapper = schemaMappers({ fieldPath: contextPath });
        const innerProps = mapInnerSchema(nestMapper, nextSchema) || {
          "data-testid": k,
          name: k,
          id: k,
          label: label || pathToLabel(fieldPath)
        };
        return [k, innerProps];
      };
      return (Object.fromEntries(
        keys.map(mapField)
      ) as unknown) as SchemaObject<T>;
    },
    array: <T>(s: ArraySchema<T>): TypeSafeArray<T> => {
      const { label } = s.describe();
      const innerSchema = reach(s, "[1]");

      function get<T>(i: number): TypeSafeEntry<T> {
        const formikPath = `${fieldPath}[${i}]`;
        const mapper = schemaMappers({
          fieldPath: formikPath
        });
        return (mapInnerSchema<T>(mapper, innerSchema) || {
          name: fieldPath,
          label: label || pathToLabel(fieldPath)
        }) as TypeSafeEntry<T>;
      }

      return {
        label: label || pathToLabel(fieldPath),
        name: fieldPath,
        id: fieldPath,
        "data-testid": fieldPath,
        get
      };
    },
    boolean: genericHandle({ fieldPath }),
    string: genericHandle({ fieldPath }),
    xmlString: genericHandle({ fieldPath }),
    number: genericHandle({ fieldPath }),
    date: genericHandle({ fieldPath }),
    mixed: genericHandle({ fieldPath })
  };
};

/**
 * Produces the set of SchemaFields<T> that has field names/identifier
 * to follow formiks conventions.
 * const s = yup.object({
 *   namedField: yup.string()
 * })
 * const fields = fromSchema(s);
 * fields.namedField.name //"namedField"
 *
 * Supports simple types, nested types, and arrays.
 * Typically will be provided to anyone using a form with a schema.
 * See:  src/collections/Form/Form.tsx
 * @param schema
 */
export function fromSchema<T>(schema: Schema<T>): SchemaFields<T> {
  const topType = schema.describe().type;
  const mapper = schemaMappers();
  // @ts-ignore
  return mapper[topType](schema);
}
